import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { 
  Check, 
  ArrowRight, 
  Monitor, 
  Wifi, 
  Users,
  Eye,
  Clock,
  Phone
} from "lucide-react";

const images = {
  hero: "https://customer-assets.emergentagent.com/job_work-space-hub/artifacts/5xi2y6ua_Conference%20Room%20%28Ocean%29.jpg",
  conference: "https://customer-assets.emergentagent.com/job_work-space-hub/artifacts/5xi2y6ua_Conference%20Room%20%28Ocean%29.jpg",
  conference2: "https://customer-assets.emergentagent.com/job_work-space-hub/artifacts/co88hf8b_Conference%20Room%202.jpg",
};

const features = [
  { icon: Eye, text: "Oceanfront View" },
  { icon: Monitor, text: '65" Display' },
  { icon: Wifi, text: "High-Speed WiFi" },
  { icon: Users, text: "Seats 8-10" },
  { icon: Clock, text: "Hourly Rental" },
];

const amenities = [
  "65\" TV for presentations and video calls",
  "High-speed WiFi and ethernet",
  "Whiteboard and markers",
  "Video conferencing capabilities",
  "Natural lighting with ocean views",
  "Professional setting for client meetings",
  "Comfortable seating for 8-10 people",
  "Coffee and refreshments available",
];

const useCases = [
  {
    title: "Client Meetings",
    description: "Impress your clients with stunning ocean views and professional amenities.",
  },
  {
    title: "Team Presentations",
    description: "Use our 65\" display for impactful presentations and training sessions.",
  },
  {
    title: "Video Conferences",
    description: "Professional backdrop and reliable internet for important video calls.",
  },
  {
    title: "Strategy Sessions",
    description: "An inspiring space for brainstorming and planning with your team.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

export const ConferencePage = () => {
  return (
    <div className="pt-20" data-testid="conference-page">
      <SEO
        title="Conference Room Rental Satellite Beach FL | Ocean View Meeting Space Brevard County"
        description="Rent our oceanview conference room in Satellite Beach. Seats 8–10 with 65&quot; display, high-speed WiFi, and video conferencing. Hourly rentals available for non-members in Brevard County."
        canonical="https://www.workcoastal.com/conference"
      />
      {/* Hero Section */}
      <section className="relative py-32 bg-brand overflow-hidden" data-testid="conference-hero">
        <div className="absolute inset-0 opacity-30">
          <img
            src={images.hero}
            alt="Conference Room"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="max-w-2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block font-sans text-sm font-medium text-sea-light uppercase tracking-wider mb-4">
              Conference Room Rental Brevard County
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl font-bold text-white leading-tight mb-6">
              Conference Room
              <span className="block text-sand-light">with Ocean View</span>
            </h1>
            <p className="font-sans text-lg text-white/80 leading-relaxed mb-8">
              Professional meeting space in Satellite Beach with oceanfront views. 
              65" TV, high-speed WiFi, and all amenities. Perfect for client meetings and presentations 
              in Brevard County and Indialantic.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="bg-sea hover:bg-sea-light text-white rounded-full px-8 btn-primary"
                  data-testid="conference-hero-cta"
                >
                  Book Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="tel:321-866-8572">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white/20 rounded-full px-8"
                  data-testid="conference-phone-button"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  (321) 866-8572
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Strip */}
      <section className="py-12 bg-white border-b border-sand/30" data-testid="conference-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16">
            {features.map((feature, index) => (
              <motion.div
                key={feature.text}
                className="flex items-center gap-3"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <feature.icon className="w-6 h-6 text-sea" strokeWidth={1.5} />
                <span className="font-sans text-sm font-medium text-brand">{feature.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 bg-white" data-testid="conference-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Images */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-4"
            >
              <img
                src={images.conference}
                alt="Conference Room with Ocean View in Satellite Beach"
                className="rounded-2xl image-hover w-full aspect-[4/3] object-cover"
              />
              <img
                src={images.conference2}
                alt="Conference Room Meeting Space Brevard County"
                className="rounded-2xl image-hover w-full aspect-[4/3] object-cover"
              />
            </motion.div>

            {/* Details */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-serif text-3xl font-bold text-brand mb-6">
                The Perfect Meeting Space
              </h2>
              <p className="font-sans text-muted-foreground leading-relaxed mb-8">
                Our conference room was designed to help you make the best impression. 
                With stunning ocean views, a 65" TV for presentations, and all the 
                technology you need, your meetings will be both productive and memorable.
              </p>

              <h3 className="font-serif text-xl font-semibold text-brand mb-4">
                Room Amenities
              </h3>
              <ul className="space-y-3 mb-8">
                {amenities.map((amenity, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-sea flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <span className="font-sans text-sm text-muted-foreground">{amenity}</span>
                  </li>
                ))}
              </ul>

              <div className="bg-sand-light/50 rounded-2xl p-6">
                <p className="font-sans text-sm text-muted-foreground mb-4">
                  <strong className="text-brand">For Members:</strong> Conference room hours 
                  included with your membership.
                </p>
                <p className="font-sans text-sm text-muted-foreground">
                  <strong className="text-brand">Hourly Rental:</strong> Available for 
                  non-members. Contact us for rates and availability.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-24 bg-sand-light/30" data-testid="conference-uses">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-2xl mx-auto mb-16"
            {...fadeUp}
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-brand mb-4">
              Perfect For
            </h2>
            <p className="font-sans text-muted-foreground leading-relaxed">
              Our conference room adapts to your needs, whether you're meeting 
              with clients or collaborating with your team.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {useCases.map((useCase, index) => (
              <motion.div
                key={useCase.title}
                className="bg-white rounded-2xl p-6 card-hover border border-sand/30"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <h3 className="font-serif text-lg font-semibold text-brand mb-2">
                  {useCase.title}
                </h3>
                <p className="font-sans text-sm text-muted-foreground">
                  {useCase.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-brand" data-testid="conference-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-4">
              Book Your Meeting Today
            </h2>
            <p className="font-sans text-lg text-white/80 leading-relaxed mb-8">
              Reserve our oceanview conference room for your next meeting. 
              Hourly rentals available for non-members.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="bg-white text-brand hover:bg-sand-light rounded-full px-8"
                  data-testid="conference-cta-button"
                >
                  Reserve Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="tel:321-866-8572">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white/30 text-white hover:bg-white/10 rounded-full px-8"
                  data-testid="conference-cta-phone"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  (321) 866-8572
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ConferencePage;
